import propz from 'propz';
import { AGE_GROUPS, AGE_GROUPS_SORTED, AGE_GROUPS_NAMING } from 'Src/consts/school';

const rankCache: { [naming: string]: { [age: string]: number } } = {};

export const resolveAgeNamingFromSchool = (school: any) => {
  const n = String(propz.get(school, ['ageGroupsNaming'], AGE_GROUPS_NAMING.ENGLISH) || '');

  if ((AGE_GROUPS_SORTED as any)[n] && (AGE_GROUPS as any)[n]) return n;

  return AGE_GROUPS_NAMING.ENGLISH;
};

export const resolveAgeNamingFromContext = (user: any, programme?: any) => {
  const activeSchool = propz.get(user, ['activeSchool'], null);
  if (activeSchool) return resolveAgeNamingFromSchool(activeSchool);

  const programmeSchool = propz.get(programme, ['school'], null);
  if (programmeSchool) return resolveAgeNamingFromSchool(programmeSchool);

  const userSchool = propz.get(user, ['school'], null);
  if (userSchool) return resolveAgeNamingFromSchool(userSchool);

  return AGE_GROUPS_NAMING.ENGLISH;
};

const getAgeRankMap = (naming: string) => {
  const key = String(naming || AGE_GROUPS_NAMING.ENGLISH);
  if (rankCache[key]) return rankCache[key];

  const sortedLabels: string[] = (AGE_GROUPS_SORTED as any)[key] || [];
  const labels: string[] = (AGE_GROUPS as any)[key] || [];

  const labelToIndex: { [k: string]: number } = {};
  labels.forEach((lbl, idx) => {
    labelToIndex[String(lbl)] = idx;
  });

  const rankByAge: { [k: string]: number } = {};
  sortedLabels.forEach((lbl, rank) => {
    const ageIdx = labelToIndex[String(lbl)];
    if (typeof ageIdx === 'number') rankByAge[String(ageIdx)] = rank;
  });

  rankCache[key] = rankByAge;
  return rankByAge;
};

export const formatSingleAgeLabel = (age: number, naming: string) => {
  const namingKey = String(naming || AGE_GROUPS_NAMING.ENGLISH);

  if (namingKey === AGE_GROUPS_NAMING.ENGLISH && age >= 1 && age <= 14) {
    return `Year ${age}`;
  }

  const labels: string[] = (AGE_GROUPS as any)[namingKey] || [];
  return String(labels[age] || age);
};

export const formatAgesCompactForSchool = (agesRaw: any, school: any) => {
  const toAges = (value: any): number[] => Array.from(new Set<number>(
    (Array.isArray(value) ? value : []).map((age: any) => Number(age)).filter((age: number) => Number.isFinite(age))
  ));
  const selected = toAges(agesRaw);
  const registrationAges = toAges(propz.get(school, ['registrationAges'], []));
  const available = registrationAges.length > 0
    ? registrationAges
    : toAges(propz.get(school, ['availableAges'], []));
  if (selected.length === 0 || (available.length > 0 && available.every(age => selected.includes(age)))) {
    return 'All ages';
  }
  const naming = resolveAgeNamingFromSchool(school);
  return formatAgesCompact(selected, naming);
};

export const formatAgesCompact = (agesRaw: any, naming: string) => {
  const arr = Array.isArray(agesRaw) ? agesRaw : [];
  const ages = arr
    .map((x: any) => Number(x))
    .filter((n: any) => Number.isFinite(n))
    .map((n: number) => Math.trunc(n));

  if (ages.length === 0) return '';

  const uniq: number[] = [];
  const seen: { [k: string]: boolean } = {};
  ages.forEach(a => {
    const k = String(a);
    if (!seen[k]) {
      seen[k] = true;
      uniq.push(a);
    }
  });

  const namingKey = String(naming || AGE_GROUPS_NAMING.ENGLISH);
  const rankByAge = getAgeRankMap(namingKey);

  uniq.sort((a, b) => {
    const ra = typeof rankByAge[String(a)] === 'number' ? rankByAge[String(a)] : 9999 + a;
    const rb = typeof rankByAge[String(b)] === 'number' ? rankByAge[String(b)] : 9999 + b;
    return ra - rb;
  });

  const groups: number[][] = [];
  let current: number[] = [];

  for (let i = 0; i < uniq.length; i++) {
    const age = uniq[i];
    const r = typeof rankByAge[String(age)] === 'number' ? rankByAge[String(age)] : null;

    if (current.length === 0) {
      current = [age];
      continue;
    }

    const prev = current[current.length - 1];
    const pr = typeof rankByAge[String(prev)] === 'number' ? rankByAge[String(prev)] : null;

    const isConsecutive = r !== null && pr !== null && r === pr + 1;

    if (isConsecutive) current.push(age);
    else {
      groups.push(current);
      current = [age];
    }
  }
  if (current.length) groups.push(current);

  return groups
    .map(g => {
      if (g.length === 1) return formatSingleAgeLabel(g[0], namingKey);
      return `${formatSingleAgeLabel(g[0], namingKey)} - ${formatSingleAgeLabel(g[g.length - 1], namingKey)}`;
    })
    .join(', ');
};

/** Compact labels specifically for team UI: "Y3-Y6", "P2-P4", "U11-U13". */
export const formatTeamAgesCompact = (agesRaw: any, naming: string) =>
  formatAgesCompact(agesRaw, naming).replace(/Year\s+(\d+)/g, 'Y$1').replace(/\s+-\s+/g, '-');

/** Compact dual tournament audience label, e.g. Y1-Y3/U6-U8. */
export const formatTournamentAges = (agesRaw: any) => {
  const ages = Array.from(
    new Set(
      (Array.isArray(agesRaw) ? agesRaw : [])
        .map((value: any) => Number(value))
        .filter((value: number) => Number.isFinite(value))
        .map((value: number) => Math.trunc(value))
    )
  ).sort((first: number, second: number) => first - second) as number[];

  if (!ages.length) return '';

  const groups: number[][] = [];
  ages.forEach(age => {
    const current = groups[groups.length - 1];
    if (current && age === current[current.length - 1] + 1) current.push(age);
    else groups.push([age]);
  });

  const compact = (values: number[], prefix: 'Y' | 'U', offset: number) => {
    const first = values[0] + offset;
    const last = values[values.length - 1] + offset;
    return values.length === 1 ? `${prefix}${first}` : `${prefix}${first}-${prefix}${last}`;
  };

  return groups.map(group => `${compact(group, 'Y', 0)}/${compact(group, 'U', 5)}`).join(', ');
};

